<template>
    <b-modal
        id="treatmentStatus"
        ref="treatmentStatus"
        :lazy="true"
        title-tag="h2"
        title="Change Status"
    >
        <b-form>
            <b-form-group
                :label-for="`status_id`"
                :label-cols="3"
                label="Status:"
            >
                <b-form-select
                    id="status_id"
                    v-model="data.status_id"
                    :options="statusList"
                    name="status_id"
                ></b-form-select>
            </b-form-group>
            <b-form-group :label-for="`room`" :label-cols="3" label="Room:">
                <b-form-select
                    id="room"
                    v-model="data.room"
                    :options="rooms"
                    name="room"
                ></b-form-select>
            </b-form-group>
            <b-form-group
                :label-for="`staffs`"
                :label-cols="3"
                label="Assigned To:"
            >
                <v-select
                    id="staffs"
                    ref="vselect"
                    v-model="data.staffs"
                    v-validate
                    :class="{ 'border-danger': errors.has('staffs') }"
                    :options="staff_options_exclude"
                    :label="'fullname'"
                    name="staffs"
                    multiple
                    aria-placeholder="true"
                    placeholder="Assigned to (optional)"
                ></v-select>
            </b-form-group>
            <b-form-group
                :label-for="`notes`"
                :label-cols="3"
                label="Notes:"
                class="mb-0"
            >
                <b-form-textarea
                    id="notes"
                    name="notes"
                    v-model="data.note"
                    class="form-control"
                    placeholder="Notes (optional)"
                    rows="3"
                ></b-form-textarea>
            </b-form-group>
        </b-form>
        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!loading"
                class="float-left"
                variant="secondary"
                @click="$refs.treatmentStatus.hide('cancel')"
                >Cancel</b-button
            >
            <div class="float-right">
                <img v-if="loading" src="assets/ajax-loader.gif" />
                <div v-if="!loading">
                    <b-button class="float-left" variant="primary" @click="save"
                        >Save</b-button
                    >
                </div>
            </div>
        </div>
    </b-modal>
</template>
<style></style>
<script>
import ScheduleHelper from "../helper";
import { difference, some, find, filter } from "lodash";
import vSelect from "vue-select";

export default {
    name: "TreatementStatus",
    components: {
        vSelect
    },
    data() {
        return {
            loading: false,
            treatment_data: {},
            data: {
                staffs: [],
                room: [],
                status_id: 0,
                note: ""
            },
            rooms: [{ value: null, text: "Select room (optional)" }],
            staffList: []
        };
    },
    mixins: [ScheduleHelper],
    computed: {
        staff_options_exclude() {
            return difference(this.staffList, this.data.staffs);
        },
        old_status() {
            return some(this.treatment_data)
                ? this.treatment_data.treatment_status[0].status_id
                : {};
        },
        is_starting() {
            return (
                (this.old_status === this.TREATMENT_STATUS.ADMITTED ||
                    this.old_status === this.TREATMENT_STATUS.PRETREATMENT) &&
                this.data.status_id > this.TREATMENT_STATUS.PRETREATMENT
            );
        },
        is_ending() {
            return this.data.status_id < 0;
        },
        /**
         * Translate index and module name to tbl_status id
         * @return {Object} status object.
         */
        statusList() {
            return some(this.$store.state.status) && this.treatment_data
                ? this.TREATMENT_STATUS_ORDER.map(status =>
                      find(this.$store.state.status, {
                          index: status,
                          module_name: "schedule.treatment"
                      })
                  )
                      .map(status => ({
                          value: status.index,
                          id: status.id,
                          text: status.value
                      }))
                      .filter(status => {
                          if (this.treatment_data.start === 0) {
                              return status.value !== -1 && status.value <= 2;
                          } else {
                              return status.value === -1 || status.value > 2;
                          }
                      })
                : [];
        }
    },
    methods: {
        open(params) {
            this.treatment_data = params;
            this.data.status_id = params.treatment_status[0].status_id;
            this.data.note = params.treatment_status[0].note;
            this.loading = true;
            Promise.all([this.getStaffList(), this.getRooms()]).finally(() => {
                this.loading = false;
            });
            this.$refs["treatmentStatus"].show();
        },
        getStaffList() {
            let self = this;
            return this.$http
                .get(process.env.ACTOR_SERVICE + "staff?getAll=1")
                .then(function (response) {
                    if (response.data.response.resources) {
                        self.staffList = response.data.response.resources;
                    }
                    self.data.staffs = self.treatment_data.staffs.map(staff => {
                        return find(self.staffList, { id: staff.id });
                    });
                    return self.staffList;
                });
        },
        getActivity(id) {
            return this.$http
                .get(process.env.BARRACUDA + "schedule/activity/" + id)
                .then(response => {
                    return response.data.response.resources;
                });
        },
        getRooms() {
            let self = this;
            let room = filter(self.treatment_data.rooms, {
                pivot: { isactive: 1 }
            });
            return this.$http
                .get(process.env.BARRACUDA + "schedule/rooms")
                .then(function (response) {
                    if (response.data.response.resources) {
                        self.rooms = [
                            { value: null, text: "Select room (optional)" },
                            ...response.data.response.resources.map(room => ({
                                value: room.id,
                                text: room.name
                            }))
                        ];
                        self.data.room = room.length > 0 ? room[0].id : null;
                    }
                });
        },
        async save() {
            let response = false;
            let valid = await this.$validator.validateAll();

            if (valid) {
                let params = {
                    start: this.is_starting
                        ? this._now().toSeconds()
                        : undefined,
                    end: this.is_ending ? this._now().toSeconds() : undefined,
                    room_id: this.data.room,
                    patient_id: this.treatment_data.patient.id,
                    status_id: this.data.status_id,
                    staff_id: this.data.staffs.map(staff => staff.id),
                    note: this.data.note
                };

                try {
                    if (params) {
                        this.loading = true;
                        response = await this.$http.put(
                            process.env.BARRACUDA +
                                "schedule/treatment-board/" +
                                this.treatment_data.id,
                            params
                        );
                        if (response.status === 200) {
                            if (this.treatment_data.appointment_id) {
                                await this.$http.post(
                                    process.env.BARRACUDA +
                                        "schedule/appointment/" +
                                        this.treatment_data.appointment_id +
                                        "/status",
                                    {
                                        status_id: find(
                                            this.$store.state.status,
                                            {
                                                index: this.data.status_id,
                                                module_name:
                                                    "schedule.treatment"
                                            }
                                        ).id,
                                        note: this.data.note
                                    }
                                );
                                this.$socket.client.emit("v-broadcast-all", {
                                    action: "refresh-all",
                                    businessId: this._setting.business_id,
                                    instance: this.uniqid(),
                                    params
                                });
                            }

                            this.loading = false;
                            this.$refs.treatmentStatus.hide();
                            this.$root.$emit("treatment-board-refresh", {
                                params: response.data.response,
                                isRelatedToFilter: true,
                                treatment_board_id: this.treatment_data.id
                            });
                            this._message("Treatment saved", "success");
                        } else {
                            this.loading = false;
                            this._message("Error saving data", "danger");
                        }
                    }
                } catch (errors) {
                    this.loading = false;
                    console.error(errors);
                    this._message("Error saving data", "danger");
                }
            }
        }
    }
};
</script>
